/**
 * Aprobación HHEE (ASIS-5351) — mappers
 * Transport DTOs → internal UI models.
 */

import { EMPTY_HOUR_VALUE } from './overtimeApproval.types';
import type {
  LC_GetOvertimeApprovalDetailListOut,
  LC_GetOvertimeApprovalMonthlyCutsListOut,
  LC_GetOvertimeJustificationTypesListOut,
  LC_ListOvertimeApprovalListOut,
  LC_OvertimeApprovalCountersOut,
  LC_OvertimeApprovalDetailItemOut,
  LC_OvertimeApprovalItemOut,
  LC_OvertimeApprovalMonthlyCutItemOut,
  LC_OvertimeJustificationTypeItemOut,
  OvertimeApprovalCounters,
  OvertimeApprovalCuts,
  OvertimeApprovalDetailPage,
  OvertimeApprovalDetailRow,
  OvertimeApprovalMonthlyCut,
  OvertimeApprovalPage,
  OvertimeApprovalRow,
  OvertimeJustificationType,
} from './overtimeApproval.types';


/** Counters DTO → internal shape, shared by the grid page and the detail page mappers. */
const mapOvertimeCounters = (
  counters: LC_OvertimeApprovalCountersOut | undefined,
): OvertimeApprovalCounters => ({
  calculated: counters?.calculatedHours ?? EMPTY_HOUR_VALUE,
  pending: counters?.pendingHours ?? EMPTY_HOUR_VALUE,
  approved: counters?.approvedHours ?? EMPTY_HOUR_VALUE,
  notApproved: counters?.notApprovedHours ?? EMPTY_HOUR_VALUE,
  // Solo el detalle entrega este contador (ASIS-5358); el listado lo deja en cero.
  justified: counters?.reviewerJustifiedHours ?? EMPTY_HOUR_VALUE,
});

const mapMonthlyCut = (
  cut: LC_OvertimeApprovalMonthlyCutItemOut,
): OvertimeApprovalMonthlyCut => ({
  monthlyCutId: cut.monthlyCutId,
  companyId: cut.companyId,
  year: cut.year,
  month: cut.month,
  monthName: cut.monthName,
  description: cut.description,
  isClosed: cut.isClosed,
  appliesToAllCompanies: cut.appliesToAllCompanies,
});

export const mapOvertimeApprovalCuts = (
  out: LC_GetOvertimeApprovalMonthlyCutsListOut,
): OvertimeApprovalCuts => ({
  monthlyCuts: (out.monthlyCuts ?? []).map(mapMonthlyCut),
  defaultMonthlyCutId: out.defaultMonthlyCutId ?? null,
  cutsSource: out.cutsSource ?? 'none',
});

const mapRow = (item: LC_OvertimeApprovalItemOut): OvertimeApprovalRow => ({
  id: item.employeeId,
  rut: item.rut,
  employeeName: item.employeeName,
  calculatedHours: item.calculatedHours ?? EMPTY_HOUR_VALUE,
  approvedHours: item.approvedHours ?? EMPTY_HOUR_VALUE,
  reviewerJustifiedHours: item.reviewerJustifiedHours ?? EMPTY_HOUR_VALUE,
  status: item.status,
});

export const mapOvertimeApprovalPage = (
  out: LC_ListOvertimeApprovalListOut,
): OvertimeApprovalPage => ({
  items: (out.items ?? []).map(mapRow),
  totalRecords: out.pagination?.totalRecords ?? 0,
  counters: mapOvertimeCounters(out.counters),
  isMonthlyCutClosed: out.isMonthlyCutClosed ?? false,
  // `null` cuando el backend no lo informa: la pantalla cae a la vista del aprobador, que es el
  // comportamiento histórico. No se asume revisor por omisión.
  viewerFlowProfile: out.viewerFlowProfile ?? null,
});

// ---------------------------------------------------------------------------
// Detail (ASIS-5355)
// ---------------------------------------------------------------------------

const mapDetailRow = (item: LC_OvertimeApprovalDetailItemOut): OvertimeApprovalDetailRow => ({
  employeeId: item.employeeId,
  rut: item.rut,
  employeeName: item.employeeName,
  workDate: item.workDate,
  overtimeType: item.overtimeType,
  overtimeTypeCode: item.overtimeTypeCode,
  calculatedHours: item.calculatedHours ?? EMPTY_HOUR_VALUE,
  approvedHours: item.approvedHours ?? EMPTY_HOUR_VALUE,
  hoursToApprove: item.hoursToApprove ?? EMPTY_HOUR_VALUE,
  status: item.status,
  scheduleLabel: item.scheduleLabel ?? null,
  scheduleId: item.scheduleId ?? null,
  // Nunca `undefined`: el SP legado descarta en silencio la fila cuando la bandera no viaja
  // explícita, así que el modelo de UI la normaliza igual que el contrato lo exige.
  isWeeklyCompensation: item.isWeeklyCompensation ?? false,
  entryTime: item.entryTime ?? null,
  exitTime: item.exitTime ?? null,
  lunchStart: item.lunchStart ?? null,
  lunchEnd: item.lunchEnd ?? null,
  approverComment: item.approverComment ?? null,
  reviewerComment: item.reviewerComment ?? null,
  reviewerJustificationType: item.reviewerJustificationType ?? null,
  isJustifiedByReviewer: item.isJustifiedByReviewer ?? false,
  reviewerStatus: item.reviewerStatus ?? null,
});

export const mapOvertimeApprovalDetailPage = (
  out: LC_GetOvertimeApprovalDetailListOut,
): OvertimeApprovalDetailPage => ({
  items: (out.items ?? []).map(mapDetailRow),
  // Sin metadata de paginación (respuesta antigua o vacía) el total es lo que llegó en la página:
  // así la tabla no anuncia más filas de las que puede mostrar.
  totalRecords: out.pagination?.totalRecords ?? (out.items ?? []).length,
  counters: mapOvertimeCounters(out.counters),
  overtimeApprovalFlowEnabled: out.overtimeApprovalFlowEnabled ?? false,
  isMonthlyCutClosed: out.isMonthlyCutClosed ?? false,
  viewerFlowProfile: out.viewerFlowProfile ?? null,
  monthlyCutYear: out.monthlyCutYear ?? 0,
  monthlyCutMonth: out.monthlyCutMonth ?? 0,
  monthlyCutDescription: out.monthlyCutDescription ?? null,
});

// ---------------------------------------------------------------------------
// Reviewer justification types (ASIS-5358)
// ---------------------------------------------------------------------------

const mapJustificationType = (
  item: LC_OvertimeJustificationTypeItemOut,
): OvertimeJustificationType => ({
  id: item.id,
  code: item.code,
  text: item.text,
  // Nunca se deriva del texto de la etiqueta: el backend la resuelve desde el código.
  requiresFreeComment: item.requiresFreeComment ?? false,
});

/**
 * Catálogo vacío ⇒ lista vacía, nunca error: es el escenario real hasta que se siembren tipos.
 *
 * Acepta las DOS formas en que puede llegar el catálogo. El contrato declara `{ items: [...] }`,
 * pero `ResponseEnvelopeHelper.TryExtractCatalogArray` (backend) detecta los DTO cuya única
 * propiedad es una lista y APLANA el envelope a `data: [...]`; `apiClient` entrega ese `data` tal
 * cual, así que en runtime llega el array desnudo. Leer sólo `out.items` devolvía siempre `[]` y el
 * selector del revisor quedaba vacío aunque el endpoint respondiera 200 con las nueve filas.
 */
export const mapOvertimeJustificationTypes = (
  out: LC_GetOvertimeJustificationTypesListOut | LC_OvertimeJustificationTypeItemOut[] | null,
): OvertimeJustificationType[] => (
  (Array.isArray(out) ? out : out?.items ?? []).map(mapJustificationType)
);
